/**
 * carta.js — LA CARTA DEL ERP en el bloque `menu` (ola 3 · T1b): conversor, pintor y aplicador.
 *
 * Tres superficies pintan la MISMA carta y las tres pasan por aquí (una sola verdad):
 *   · el EDITOR (`blocks/menu.js`, rama ERP) pinta la FOTO guardada en `props.categories`;
 *   · PUBLICAR (`compileSiteHtml` llama al mismo renderer) la hornea para buscadores y para quien
 *     no ejecuta JS;
 *   · el runtime VIVO (`core/carta-live-runtime.js`) pide `/site_carta/` y repinta con la carta
 *     del momento, sin volver a publicar.
 *
 * Módulo PURO: ES2019 (sin `?.` ni `??`: corre en el node 12 de los tests), sin DOM ni `window`,
 * y solo importa ./dom.js. Por eso se prueba en node (tests/_carta.mjs); el bloque no se puede
 * (hereda el `?.` de core/store.js).
 *
 * CONTRATOS
 *   fotoDesdeCarta(carta, {secciones}) → {categories, alergenos, local}
 *       `carta` = la de `web/lib/carta_publica.carta` con las fotos ya en URL (`con_urls`): la que
 *       devuelven `api/carta/foto/` (editor) y `/site_carta/` (vivo).
 *       `categories` = [{id, name, items:[{id, name, desc, price, foto, alergenos:[c], menu,
 *       pasos:[{nombre, opciones:[nombres]}]}]}] → la forma de SIEMPRE del bloque (name/desc/price,
 *       la que leen el SEO, el saneo y el traductor) + lo del ERP. `price` es `precio_txt` (ya
 *       formateado en el idioma pedido), nunca `precio`. Las opciones de un paso llegan resueltas a
 *       NOMBRES con un mapa de TODA la carta (una opción puede ser un plato de otra sección), hecho
 *       ANTES de filtrar secciones. Ni grupos de modificadores ni agotados. Ninguna clave empieza por
 *       `_` (`views.limpiar_json_basura` las borra al guardar).
 *       `alergenos` = {c:{t, n, svg, col, tinta, tinte}} solo de los códigos que salen en la foto.
 *       `local` = el nombre del local (para el sello del editor).
 *   htmlCarta(categories, alergenos, {ocultarPrecios, prefijo, compacta, modoAlergenos, vista,
 *       maxPlatos, pasosMenu, textos}) → el INTERIOR de `.menu-categories`, con las MISMAS clases
 *       que el modo manual (site.css, v1/v2 y `menuAnim` valen tal cual) y sin `data-field` ni
 *       `contenteditable`. Sin las opciones de PRESENTACIÓN, byte a byte el de siempre.
 *   presentacionCarta(source) → la PRESENTACIÓN que el cliente eligió en «Personalizar la carta»
 *       (`editor/carta-panel.js`), siempre válida. Las demás funciones la reciben ya normalizada.
 *   sourceConPresentacion(source, borrador, idsSecciones, idsPlatos) → la `source` que guarda el panel.
 *   vivoDePresentacion / presentacionDeVivo → la misma presentación en los parámetros cortos de
 *       `data-carta-live` (los escribe `blocks/menu.js`, los lee `core/carta-live-runtime.js`).
 *   esSvgSeguro(s) → ¿se puede meter este interior de `<svg>` en el HTML tal cual?
 *   aplicarFoto(props, resp, ahoraIso) → props (MUTA y devuelve): qué hace la respuesta de
 *       `api/carta/foto/` con la foto guardada. La usan el editor (carta-erp.js) y publicar
 *       (viewer.js): una regla, dos consumidores.
 *   PRESUPUESTO_CARTA → lo que la carta puede ocupar en el HTML de UNA página.
 *
 * SIN TEXTOS FIJOS: todo lo visible sale de los datos (nombres, precios, alérgenos y pasos del ERP).
 * El publicado estático no recibe `UI_LABELS`, y la Q9 de `carta-qr` prohíbe llevar ahí las claves de
 * `CARTA_LABELS`: por eso no hay «Alérgenos:», «Elige…» ni nada parecido. Las dos frases de la
 * presentación (el aviso de «Sin alérgenos» y «Ver todos») llegan en `textos`, ya traducidas: las
 * pone `blocks/menu.js` con `t()` al pintar y viajan HORNEADAS en `data-carta-live` (como las de las
 * secciones de tienda en `data-tienda`). Sin su texto, esa pieza no se pinta.
 */
import { escapeHtml } from './dom.js';

/**
 * El tope del saneo de publicar (512 KB y 20 000 `<`) es por PÁGINA entera y falla CERRADO (413: no
 * se publica nada). Una carta grande con los iconos en línea se lo comía sola (400 platos con SVG
 * pasaban de 800 K), así que la carta tiene su presupuesto: si la versión completa no cabe, se repinta
 * en `compacta` (sin descripciones y con los alérgenos como píldoras de texto, sin iconos).
 */
export const PRESUPUESTO_CARTA = { caracteres: 300000, etiquetas: 8000 };

// Etiquetas que puede traer el dibujo de un alérgeno (el contrato de `web/lib/alergenos.py::ICONOS` es
// «solo <path>»; se admite algo más de geometría pura por si algún día cambia).
const _SVG_ETIQUETAS = { path: 1, circle: 1, rect: 1, line: 1, polyline: 1, polygon: 1, ellipse: 1, g: 1 };
const _SVG_MAX = 8000;
// Interrogante neutro: el mismo dibujo que `alergenos.icono()` da a un código desconocido. Es un icono,
// no un texto: nunca se oculta un alérgeno, a lo sumo se pinta sin su dibujo.
const _ICONO_NEUTRO = '<path d="M3.6 12a8.4 8.4 0 1 0 16.8 0a8.4 8.4 0 1 0-16.8 0"/><path d="M12 16.6v.1"/>';
const _HEX = /^#[0-9a-fA-F]{3,8}$/;

function _propia(obj, clave) {
    return !!obj && Object.prototype.hasOwnProperty.call(obj, clave);
}

/** Texto plano de un valor del ERP: cadenas y números; lo demás, ''. */
function _txt(v) {
    if (typeof v === 'string') return v;
    if (typeof v === 'number' && isFinite(v)) return String(v);
    return '';
}

function _lista(v) {
    return Array.isArray(v) ? v : [];
}

function _obj(v) {
    return (v && typeof v === 'object' && !Array.isArray(v)) ? v : null;
}

/** La URL de la foto si es `https?://…` o una ruta `/…` del propio host, sin blancos, comillas ni
 *  ángulos; si no (javascript:, data:, `//otro-host`, basura), ''. */
function _urlFoto(u) {
    if (typeof u !== 'string') return '';
    const s = u.trim();
    return (/^https?:\/\/[^\s"'<>`]+$/i.test(s) || /^\/(?!\/)[^\s"'<>`]*$/.test(s)) ? s : '';
}

/** El prefijo de los ids del sprite (`mc{index}`), en `[a-z0-9-]`: va dentro de `id="…"` y de
 *  `href="#…"`, y dos cartas en la misma página no pueden compartirlo. */
function _prefijo(p) {
    const s = String(p == null ? '' : p).toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 20);
    return s || 'mc';
}

/** Una etiqueta COMPLETA: su nombre, atributos `nombre="valor"` (o con comilla simple) sin `<` ni `>`
 *  dentro del valor, y su `>`. Con el tope de `_SVG_MAX`, el retroceso de la regex está acotado. */
const _SVG_ETIQUETA = /<\/?([a-zA-Z][\w:-]*)(?:\s+[a-zA-Z_:][\w:.-]*\s*=\s*(?:"[^"<>]*"|'[^'<>]*'))*\s*\/?>/g;

/**
 * ¿Es seguro meter este INTERIOR de `<svg>` en el HTML tal cual? Solo geometría: `path`, `circle`,
 * `rect`, `line`, `polyline`, `polygon`, `ellipse` y `g`, sin manejadores `on…`, sin `href`, sin
 * `url(`, sin `style`, sin `<script` y sin entidades. Todo `<` tiene que abrir o cerrar una de esas
 * etiquetas (fuera comentarios, CDATA, `</symbol>` o `</svg>` que cerrarían el contenedor), y cada
 * etiqueta tiene que estar COMPLETA: sus comillas cerradas y su `>`, con solo blancos entre una y
 * otra. Una etiqueta o una comilla sin cerrar (`<path d="M0 0"`, `<path d="M0 0>`) no ejecuta nada,
 * pero se tragaría el `</symbol>` que la sigue y descuadraría el sprite de toda la carta.
 * Si no pasa, quien pinta usa el icono neutro.
 */
export function esSvgSeguro(s) {
    if (typeof s !== 'string' || !s.trim() || s.length > _SVG_MAX) return false;
    const bajo = s.toLowerCase();
    const prohibidos = ['href', 'url(', 'style', 'script', 'javascript', '&'];
    for (let i = 0; i < prohibidos.length; i++) {
        if (bajo.indexOf(prohibidos[i]) !== -1) return false;
    }
    // Un atributo `on…` (onload, onclick, onbegin…): tras un blanco, una comilla o una barra.
    if (/[\s"'\/]on[a-z]+\s*=/i.test(s)) return false;
    // Lo que queda FUERA de las etiquetas completas: solo blancos. Un `<` que no abre una etiqueta
    // completa (de la lista o no) se queda aquí y la descarta.
    _SVG_ETIQUETA.lastIndex = 0;
    let resto = '';
    let desde = 0;
    let m;
    while ((m = _SVG_ETIQUETA.exec(s))) {
        if (!_propia(_SVG_ETIQUETAS, m[1].toLowerCase())) return false;
        resto += s.slice(desde, m.index);
        desde = m.index + m[0].length;
    }
    resto += s.slice(desde);
    return !/\S/.test(resto);
}

/** `null` = todas las secciones; si no, un mapa id→true de las elegidas (`secciones: [ids]`). */
function _filtroSecciones(secciones) {
    if (!Array.isArray(secciones)) return null;
    const filtro = Object.create(null);
    secciones.forEach((id) => { if (id != null) filtro[String(id)] = true; });
    return filtro;
}

// ── PRESENTACIÓN («Personalizar la carta», 13-10-2026) ──────────────────────────────────────────────
// Lo elige el cliente en `editor/carta-panel.js` (o se lo propone la IA) y vive en `props.source`:
//   alergenos: 'nombre' (de siempre) | 'simbolo' (solo el icono, con leyenda al final) | 'no' (ninguno,
//              con el aviso de preguntar al personal: sin chips, un alérgico no debe leer «no lleva nada»)
//   vista:     'lista' (de siempre) | 'pestanas' (una sección a la vez)
//   max_platos: 0 (todos) | 4 | 6 | 8 por sección; el resto, detrás de «Ver todos»
//   menu_pasos: false = los menús sin la lista de opciones de cada paso
//   ocultos / destacados: ids de plato. Ocultar es una lista NEGRA a propósito: un plato que se da de
//              alta en el ERP sale solo. Los destacados van primero en su sección, con una estrella.

export const MODOS_ALERGENOS = ['nombre', 'simbolo', 'no'];
export const VISTAS_CARTA = ['lista', 'pestanas'];
export const MAX_PLATOS = [0, 4, 6, 8];
const _MAX_IDS = 2000;
const _MAX_TEXTO = 200;

/** Ids de plato en texto (`^\d{1,10}$`), sin repetir y con tope; lo demás se descarta. */
function _ids(v) {
    const out = [];
    const vistos = Object.create(null);
    _lista(v).forEach((x) => {
        const s = (typeof x === 'number' && isFinite(x)) ? String(x) : (typeof x === 'string' ? x : '');
        if (/^\d{1,10}$/.test(s) && !vistos[s] && out.length < _MAX_IDS) {
            vistos[s] = true;
            out.push(s);
        }
    });
    return out;
}

function _mapaIds(lista) {
    const m = Object.create(null);
    _ids(lista).forEach((id) => { m[id] = true; });
    return m;
}

/** La PRESENTACIÓN de una carta ERP desde `props.source`: siempre válida (lo que falta o no se
 *  entiende, como siempre). */
export function presentacionCarta(src) {
    const s = _obj(src) || {};
    const max = Number(s.max_platos);
    return {
        alergenos: MODOS_ALERGENOS.indexOf(s.alergenos) >= 0 ? s.alergenos : 'nombre',
        vista: s.vista === 'pestanas' ? 'pestanas' : 'lista',
        max: MAX_PLATOS.indexOf(max) > 0 ? max : 0,
        pasos: s.menu_pasos !== false,
        ocultos: _ids(s.ocultos),
        destacados: _ids(s.destacados),
    };
}

/** Un texto de la presentación (ya traducido): plano, recortado y con tope; si no, ''. */
function _textoPres(v) {
    return typeof v === 'string' ? v.trim().slice(0, _MAX_TEXTO) : '';
}

/**
 * La presentación en los parámetros CORTOS de `data-carta-live`. Solo lo que no es de siempre: una
 * carta sin personalizar lleva el atributo de antes, byte a byte. `textos` = {aviso, mas}, ya traducidos.
 *   a: 1 símbolo · 2 sin alérgenos   v: 1 pestañas   n: 4|6|8   m: 0 menús sin pasos
 *   o / d: ids ocultos / destacados  t: {a: aviso, m: «Ver todos»} (solo los que se usan)
 */
export function vivoDePresentacion(pres, textos) {
    const p = _obj(pres) || presentacionCarta(null);
    const tx = _obj(textos) || {};
    const out = {};
    if (p.alergenos === 'simbolo') out.a = 1;
    if (p.alergenos === 'no') out.a = 2;
    if (p.vista === 'pestanas') out.v = 1;
    if (p.max > 0) out.n = p.max;
    if (p.pasos === false) out.m = 0;
    if (p.ocultos && p.ocultos.length) out.o = p.ocultos.slice();
    if (p.destacados && p.destacados.length) out.d = p.destacados.slice();
    const t = {};
    if (p.alergenos === 'no' && _textoPres(tx.aviso)) t.a = _textoPres(tx.aviso);
    if (p.max > 0 && _textoPres(tx.mas)) t.m = _textoPres(tx.mas);
    if (t.a || t.m) out.t = t;
    return out;
}

/** Lo contrario de `vivoDePresentacion`, VALIDADO (el atributo viaja en HTML publicado y se edita a
 *  mano): `{pres, textos}`. Lo que no se entiende, como siempre. */
export function presentacionDeVivo(d) {
    const v = _obj(d) || {};
    const t = _obj(v.t) || {};
    return {
        pres: presentacionCarta({
            alergenos: v.a === 1 ? 'simbolo' : (v.a === 2 ? 'no' : 'nombre'),
            vista: v.v === 1 ? 'pestanas' : 'lista',
            max_platos: v.n,
            menu_pasos: v.m !== 0,
            ocultos: v.o,
            destacados: v.d,
        }),
        textos: { aviso: _textoPres(t.a), mas: _textoPres(t.m) },
    };
}

/** Las opciones de `htmlCarta` para una presentación (una sola traducción, para el bloque y el vivo). */
export function opcionesDePresentacion(pres, textos) {
    const p = _obj(pres) || presentacionCarta(null);
    const tx = _obj(textos) || {};
    return { modoAlergenos: p.alergenos, vista: p.vista, maxPlatos: p.max, pasosMenu: p.pasos,
             textos: { aviso: _textoPres(tx.aviso), mas: _textoPres(tx.mas) } };
}

/**
 * La `source` que GUARDA «Personalizar la carta» (`editor/carta-panel.js`): la de antes con la
 * presentación del borrador `b` = {alergenos, vista, max, pasos, precios, secciones: null|[ids],
 * ocultos, destacados}. Pura: no muta `src`.
 *   · Los valores de siempre se BORRAN: una carta que vuelve a lo de siempre queda como estaba.
 *   · `secciones`: 'todas' si están todas marcadas (una sección NUEVA del ERP sale sola); si no, las
 *     marcadas en el orden de la carta. Sin `idsSecciones` (la carta no se pudo leer), las de antes.
 *   · `ocultos` / `destacados`: solo ids que están en la carta (sin `idsPlatos`, tal cual); un plato
 *     oculto no es destacado.
 */
export function sourceConPresentacion(src, b, idsSecciones, idsPlatos) {
    const out = Object.assign({}, _obj(src) || {});
    const br = _obj(b) || {};
    out.ocultar_precios = br.precios === false;
    const enCarta = (lista, validos) => {
        const ids = _ids(lista);
        return Array.isArray(validos) ? ids.filter((id) => validos.indexOf(id) !== -1) : ids;
    };
    if (Array.isArray(idsSecciones)) {
        const todas = _ids(idsSecciones);
        const marcadas = br.secciones === null || br.secciones === undefined ? todas
            : todas.filter((id) => _ids(br.secciones).indexOf(id) !== -1);
        out.secciones = marcadas.length === todas.length ? 'todas' : marcadas;
    }
    const pres = presentacionCarta({ alergenos: br.alergenos, vista: br.vista, max_platos: br.max,
                                     menu_pasos: br.pasos });
    const poner = (clave, valor, deSiempre) => {
        if (valor === deSiempre) delete out[clave];
        else out[clave] = valor;
    };
    poner('alergenos', pres.alergenos, 'nombre');
    poner('vista', pres.vista, 'lista');
    poner('max_platos', pres.max, 0);
    poner('menu_pasos', pres.pasos, true);
    const ocultos = enCarta(br.ocultos, idsPlatos);
    const destacados = enCarta(br.destacados, idsPlatos).filter((id) => ocultos.indexOf(id) === -1);
    if (ocultos.length) out.ocultos = ocultos; else delete out.ocultos;
    if (destacados.length) out.destacados = destacados; else delete out.destacados;
    return out;
}

/**
 * La FOTO de la carta del ERP con la forma del bloque `menu` (ver el contrato arriba). Pura: no muta
 * `carta`. Una carta vacía o que no es un objeto → `{categories: [], alergenos: {}, local: ''}`.
 * `opciones.ocultos` quita esos platos (y sus alérgenos, si ya no sale ninguno que los lleve);
 * `opciones.destacados` los pone PRIMERO en su sección (en el orden del ERP) con `destacado: true`.
 */
export function fotoDesdeCarta(carta, opciones) {
    const alergenos = Object.create(null);
    const vacia = { categories: [], alergenos: alergenos, local: '' };
    const c = _obj(carta);
    if (!c) return vacia;
    const secciones = _lista(c.secciones);
    const catalogo = _obj(c.alergenos) || {};
    const local = _obj(c.local) ? _txt(c.local.nombre) : '';

    // id → nombre de TODA la carta, antes de filtrar: las opciones de un paso de menú son platos
    // de cualquier sección (un «Primero» puede ser un entrante que el cliente no quiere enseñar aparte).
    const nombres = Object.create(null);
    secciones.forEach((sec) => {
        _lista(sec && sec.platos).forEach((pl) => {
            if (pl && pl.id != null) nombres[String(pl.id)] = _txt(pl.nombre);
        });
    });

    const filtro = _filtroSecciones(opciones && opciones.secciones);
    const ocultos = _mapaIds(opciones && opciones.ocultos);
    const destacados = _mapaIds(opciones && opciones.destacados);
    const usados = [];
    const vistos = Object.create(null);
    const categories = [];
    secciones.forEach((sec) => {
        if (!_obj(sec)) return;
        if (filtro && !filtro[String(sec.id)]) return;
        const items = [];
        const primero = [];
        _lista(sec.platos).forEach((pl) => {
            if (!_obj(pl)) return;
            if (pl.id != null && ocultos[String(pl.id)]) return;
            const codigos = [];
            _lista(pl.alergenos).forEach((cod) => {
                if (typeof cod !== 'string' || !cod) return;
                if (codigos.indexOf(cod) === -1) codigos.push(cod);
                if (!vistos[cod]) { vistos[cod] = true; usados.push(cod); }
            });
            const pasos = [];
            _lista(pl.pasos).forEach((ps) => {
                if (!_obj(ps)) return;
                const ops = [];
                _lista(ps.opciones).forEach((id) => {
                    const n = id != null ? nombres[String(id)] : '';
                    if (n) ops.push(n);
                });
                // Un paso sin opciones que enseñar es un menú mal configurado (el terminal tampoco
                // lo deja pedir): no se pinta su nombre suelto.
                if (ops.length) pasos.push({ nombre: _txt(ps.nombre), opciones: ops });
            });
            const item = {
                id: pl.id != null ? pl.id : '',
                name: _txt(pl.nombre),
                desc: _txt(pl.descripcion),
                price: _txt(pl.precio_txt),
                foto: _urlFoto(pl.foto),
                alergenos: codigos,
                menu: pl.menu === true,
                pasos: pasos,
            };
            // La clave solo existe en un destacado: la foto de una carta sin personalizar no cambia.
            if (pl.id != null && destacados[String(pl.id)]) {
                item.destacado = true;
                primero.push(item);
            } else {
                items.push(item);
            }
        });
        const todos = primero.concat(items);
        if (todos.length) categories.push({ id: sec.id != null ? sec.id : '', name: _txt(sec.nombre), items: todos });
    });

    // Solo los códigos que salen en la foto. Un código sin ficha en el catálogo (no debería: el ERP
    // manda la de todos, también la de los de la casa) entra vacío y se pinta con el icono neutro.
    usados.forEach((cod) => {
        const a = _propia(catalogo, cod) ? (_obj(catalogo[cod]) || {}) : {};
        alergenos[cod] = { t: _txt(a.t), n: _txt(a.n), svg: _txt(a.svg), col: _txt(a.col),
                           tinta: _txt(a.tinta), tinte: _txt(a.tinte) };
    });
    return { categories: categories, alergenos: alergenos, local: local };
}

function _icono(a) {
    const svg = a && typeof a.svg === 'string' ? a.svg : '';
    return esSvgSeguro(svg) ? svg : _ICONO_NEUTRO;
}

// `soloSimbolo`: el texto sigue ahí para el lector de pantalla (`.menu-al-txt`, oculto a la vista) y el
// nombre completo en el `title`; la leyenda del final dice qué es cada icono.
function _chip(a, i, prefijo, compacta, soloSimbolo) {
    const e = a || {};
    const estilo = [['--al-c', e.col], ['--al-t', e.tinta], ['--al-b', e.tinte]]
        .filter((par) => typeof par[1] === 'string' && _HEX.test(par[1]))
        .map((par) => par[0] + ':' + par[1]).join(';');
    const nombre = _txt(e.n);
    const texto = escapeHtml(_txt(e.t));
    return '<li class="menu-alergeno"' + (nombre ? ' title="' + escapeHtml(nombre) + '"' : '')
        + (estilo ? ' style="' + estilo + '"' : '') + '>'
        + (compacta ? '' : '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">'
            + '<use href="#' + prefijo + '-a' + i + '"></use></svg>')
        + (soloSimbolo ? '<span class="menu-al-txt">' + texto + '</span>' : texto) + '</li>';
}

// Estrella del plato DESTACADO (decorativa: el orden ya lo dice).
const _ESTRELLA = '<svg class="menu-destacado-ic" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">'
    + '<path d="M12 3.2l2.6 5.5 6 .8-4.4 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6-4.4-4.2 6-.8z"/></svg>';

function _pasos(pasos) {
    return _lista(pasos).map((ps) => {
        if (!_obj(ps)) return '';
        const ops = _lista(ps.opciones).map(_txt).filter(Boolean);
        if (!ops.length) return '';
        const nombre = _txt(ps.nombre);
        return '<p class="menu-dish-pasos">'
            + (nombre ? '<span class="menu-paso">' + escapeHtml(nombre) + ':</span> ' : '')
            + ops.map(escapeHtml).join(' · ') + '</p>';
    }).join('');
}

/**
 * `o` = {prefijo, ocultar, compacta, modoAl, vista, max, pasos, textos:{aviso, mas}} (ya validado en
 * `htmlCarta`). Con los valores de siempre (`nombre`, `lista`, 0, true) sale byte a byte lo de antes.
 */
function _pintar(categories, alergenos, o) {
    const cats = _lista(categories);
    const catalogo = _obj(alergenos) || {};
    const prefijo = o.prefijo;
    const compacta = o.compacta;
    const conAlergenos = o.modoAl !== 'no';
    // Sin iconos (compacta) no hay símbolo que enseñar solo: se queda la píldora con su texto.
    const soloSimbolo = o.modoAl === 'simbolo' && !compacta;
    // Índice de cada código en orden de aparición: el id del símbolo y la referencia del chip salen
    // de AQUÍ, nunca del código (texto libre del ERP: nunca llega al HTML).
    const indice = Object.create(null);
    const orden = [];
    if (conAlergenos) {
        cats.forEach((cat) => {
            _lista(cat && cat.items).forEach((it) => {
                _lista(it && it.alergenos).forEach((cod) => {
                    if (typeof cod === 'string' && cod && indice[cod] === undefined) {
                        indice[cod] = orden.length;
                        orden.push(cod);
                    }
                });
            });
        });
    }

    let html = '';
    // «Sin alérgenos»: el aviso va ARRIBA, antes del primer plato (y de las pestañas).
    if (!conAlergenos && o.textos.aviso) {
        html += '<p class="menu-aviso-alergenos">' + escapeHtml(o.textos.aviso) + '</p>';
    }
    if (!compacta && orden.length) {
        // UN sprite por bloque: cada chip lo referencia con <use>, en vez de repetir el dibujo.
        html += '<svg class="menu-al-sprite" aria-hidden="true" focusable="false" style="display:none">'
            + orden.map((cod, i) => '<symbol id="' + prefijo + '-a' + i + '" viewBox="0 0 24 24">'
                + _icono(_propia(catalogo, cod) ? catalogo[cod] : null) + '</symbol>').join('')
            + '</svg>';
    }
    // Pestañas: solo con más de una sección (una sola no tiene a qué cambiar). Cada pestaña y su
    // panel se emparejan por posición (`data-menu-tab` ↔ `data-menu-panel`); los ids, por el prefijo
    // del bloque. Lo que cambia de pestaña es `core/block-actions.js` (delegación, nada de `on*`).
    const validas = cats.filter((cat) => !!_obj(cat));
    const pestanas = o.vista === 'pestanas' && validas.length > 1;
    if (pestanas) {
        html += '<div class="menu-tabs" role="tablist">' + validas.map((cat, k) =>
            '<button type="button" class="menu-tab' + (k === 0 ? ' is-active' : '') + '" role="tab" id="'
            + prefijo + '-t' + k + '" aria-controls="' + prefijo + '-p' + k + '" aria-selected="'
            + (k === 0 ? 'true' : 'false') + '" data-menu-tab="' + k + '">' + escapeHtml(_txt(cat.name)) + '</button>').join('')
            + '</div>';
    }
    let k = 0;
    cats.forEach((cat, ci) => {
        if (!_obj(cat)) return;
        const items = _lista(cat.items);
        // «Ver todos»: solo con su texto y si de verdad sobra algún plato.
        const limite = (o.max > 0 && o.textos.mas && items.length > o.max) ? o.max : 0;
        const platos = items.map((it, ii) => {
            if (!_obj(it)) return '';
            const foto = _urlFoto(it.foto);
            const precio = o.ocultar ? '' : _txt(it.price);
            const desc = compacta ? '' : _txt(it.desc);
            const suyos = [];
            _lista(it.alergenos).forEach((cod) => {
                if (typeof cod === 'string' && indice[cod] !== undefined && suyos.indexOf(cod) === -1) suyos.push(cod);
            });
            const chips = suyos.length
                ? '<ul class="menu-dish-alergenos' + (soloSimbolo ? ' solo-simbolo' : '') + '">' + suyos.map((cod) =>
                    _chip(_propia(catalogo, cod) ? catalogo[cod] : null, indice[cod], prefijo, compacta, soloSimbolo)).join('') + '</ul>'
                : '';
            const clases = (foto ? ' con-foto' : '') + (it.destacado === true ? ' menu-dish-destacado' : '')
                + (limite && ii >= limite ? ' menu-dish-mas' : '');
            return '<div class="menu-dish stagger-item' + clases + '" style="--stagger-i:' + ii + ';">'
                + (foto ? '<img class="menu-dish-img" src="' + escapeHtml(foto) + '" alt="" loading="lazy" decoding="async">' : '')
                + '<div class="menu-dish-head">' + (it.destacado === true ? _ESTRELLA : '')
                + '<span class="dish-name">' + escapeHtml(_txt(it.name)) + '</span>'
                // Sin precio no hay líder de puntos ni hueco: en el editor `.dish-price:empty` pintaría «Precio».
                + (precio ? '<span class="menu-leader" aria-hidden="true"></span><span class="dish-price">' + escapeHtml(precio) + '</span>' : '')
                + '</div>'
                + (desc ? '<p class="dish-desc">' + escapeHtml(desc) + '</p>' : '')
                + chips + (o.pasos ? _pasos(it.pasos) : '')
                + '</div>';
        }).join('');
        const verTodos = limite
            ? '<button type="button" class="menu-ver-todos" aria-expanded="false">'
                + escapeHtml(o.textos.mas) + ' (' + items.length + ')</button>'
            : '';
        const panel = pestanas
            ? ' id="' + prefijo + '-p' + k + '" role="tabpanel" aria-labelledby="' + prefijo + '-t' + k
                + '" data-menu-panel="' + k + '"'
            : '';
        html += '<div class="menu-category stagger-item' + (pestanas && k === 0 ? ' is-active' : '') + '"' + panel
            + ' style="--stagger-i:' + ci + ';">'
            + '<h3 class="menu-cat-name">' + escapeHtml(_txt(cat.name)) + '</h3>'
            + '<div class="menu-items">' + platos + '</div>' + verTodos + '</div>';
        k++;
    });
    // «Solo el símbolo»: la leyenda de los iconos que salen, con su nombre.
    if (soloSimbolo && orden.length) {
        html += '<ul class="menu-leyenda-alergenos">' + orden.map((cod, i) =>
            _chip(_propia(catalogo, cod) ? catalogo[cod] : null, i, prefijo, false, false)).join('') + '</ul>';
    }
    return html;
}

function _cabe(html) {
    return html.length <= PRESUPUESTO_CARTA.caracteres
        && (html.match(/</g) || []).length <= PRESUPUESTO_CARTA.etiquetas;
}

/**
 * El INTERIOR de `.menu-categories` para una foto de la carta.
 * `compacta`: `true` la fuerza; `false` pinta SIEMPRE la completa (el runtime vivo: no hornea nada);
 * sin indicar, completa y, si no cabe en `PRESUPUESTO_CARTA`, compacta.
 */
export function htmlCarta(categories, alergenos, opciones) {
    const o = opciones || {};
    const tx = _obj(o.textos) || {};
    const max = Number(o.maxPlatos);
    const base = {
        prefijo: _prefijo(o.prefijo),
        ocultar: o.ocultarPrecios === true,
        modoAl: MODOS_ALERGENOS.indexOf(o.modoAlergenos) >= 0 ? o.modoAlergenos : 'nombre',
        vista: o.vista === 'pestanas' ? 'pestanas' : 'lista',
        max: MAX_PLATOS.indexOf(max) > 0 ? max : 0,
        pasos: o.pasosMenu !== false,
        textos: { aviso: _textoPres(tx.aviso), mas: _textoPres(tx.mas) },
    };
    const con = (compacta) => Object.assign({}, base, { compacta: compacta });
    if (o.compacta === true) return _pintar(categories, alergenos, con(true));
    const completa = _pintar(categories, alergenos, con(false));
    if (o.compacta === false || _cabe(completa)) return completa;
    return _pintar(categories, alergenos, con(true));
}

/** ¿Este bloque `menu` está conectado a la carta del ERP? */
export function esCartaErp(props) {
    return !!(props && typeof props === 'object' && props.source && typeof props.source === 'object'
        && props.source.mode === 'erp');
}

/**
 * T3b · fase 2: las cartas del ERP de una web que hay que traducir al PUBLICAR, y a qué idiomas →
 * `[{rid, idiomas}]`, por orden de aparición. Cada carta va a los idiomas de las páginas donde está
 * (la `-en` de una web en español, o la de inicio de una web en inglés); el español no se pide nunca,
 * porque es el idioma de los datos del ERP. `paginas` = `[{lang, blocks}]`, con el idioma de cada
 * página ya resuelto (`pages/viewer.js::_langDePagina`). PURA: solo lee la entrada.
 */
export function cartasPorTraducir(paginas) {
    const orden = [];
    const idiomas = Object.create(null);
    (Array.isArray(paginas) ? paginas : []).forEach((p) => {
        const lang = (p && typeof p.lang === 'string') ? p.lang.trim().toLowerCase() : '';
        if (!lang || lang === 'es') return;
        ((p && Array.isArray(p.blocks)) ? p.blocks : []).forEach((b) => {
            if (!b || b.type !== 'menu' || !esCartaErp(b.props)) return;
            const r = b.props.source.restaurant_id;
            const rid = (r == null) ? '' : String(r).trim();
            if (!rid) return;
            if (!idiomas[rid]) {
                idiomas[rid] = [];
                orden.push(rid);
            }
            if (idiomas[rid].indexOf(lang) === -1) idiomas[rid].push(lang);
        });
    });
    return orden.map((rid) => ({ rid: rid, idiomas: idiomas[rid].slice() }));
}

/**
 * En cuántas páginas va alguna carta del ERP, y de cuántos idiomas → `{paginas, idiomas}`. Cada página
 * guarda (y al publicar, hornea) la suya ENTERA, y Guardar/Publicar lo manda todo en UN POST con el tope
 * de 2,5 MB: con varios idiomas es lo que más pesa (H5 de la revisión de T1b). Lo nombra el aviso de
 * «no cabe» (`imagenes-incrustadas.js::mensajeNoCabe`). `paginas` = `[{lang, blocks}]`, como en
 * `cartasPorTraducir`. PURA: solo lee la entrada.
 */
export function paginasConCarta(paginas) {
    const idiomas = Object.create(null);
    let n = 0;
    let m = 0;
    (Array.isArray(paginas) ? paginas : []).forEach((p) => {
        const blocks = (p && Array.isArray(p.blocks)) ? p.blocks : [];
        if (!blocks.some((b) => b && b.type === 'menu' && esCartaErp(b.props))) return;
        n++;
        const lang = (p && typeof p.lang === 'string') ? p.lang.trim().toLowerCase() : '';
        if (!idiomas[lang]) {
            idiomas[lang] = true;
            m++;
        }
    });
    return { paginas: n, idiomas: m };
}

/**
 * T3b · fase 2: qué hace el editor, al publicar, con la respuesta de `api/carta/traducir/` para una carta:
 *   · 'lanzada' → avisa de que se está traduciendo y PARA (una traducción a la vez por cuenta);
 *   · 'seguir'  → nada que decir y a por la siguiente carta: ya estaba traducida (`nada`), está apagada
 *                 o el local no existe aquí (un bloque pegado de otra cuenta);
 *   · 'avisar'  → enseña su `texto` y PARA (en curso, cupo, ocupado, sin permiso…);
 *   · 'parar'   → sin texto (sesión, red, una respuesta rara): calla y PARA.
 * PURA.
 */
export function pasoTraduccionCarta(r) {
    if (!r || typeof r !== 'object') return 'parar';
    if (r.estado === 'lanzada') return 'lanzada';
    if (r.estado === 'nada' || r.error === 'carta_apagada' || r.error === 'sin_local') return 'seguir';
    return (typeof r.texto === 'string' && r.texto) ? 'avisar' : 'parar';
}

/**
 * Deja la carta ERP de `props` PENDIENTE (sin foto) para que el editor la vuelva a pedir. Lo que llega
 * de fuera no puede enseñar una foto que no es suya: un bloque PEGADO (el portapapeles es
 * `sessionStorage`, compartido por todos los tenants del mismo origen) o el de una página ESPEJO (su
 * idioma no es el de la base). MUTA y devuelve.
 */
export function cartaPendiente(props) {
    if (!esCartaErp(props)) return props;
    props.categories = [];
    props.carta_alergenos = {};
    props.source.estado = '';
    props.source.actualizada = '';
    return props;
}

/**
 * Aplica la respuesta de `api/carta/foto/` ({ok, estado, carta?}) a la foto guardada. MUTA y devuelve.
 *   · `ok` (con carta) → la foto nueva (`fotoDesdeCarta` con las secciones, los platos ocultos y los
 *     destacados del bloque), el nombre del local y `actualizada = ahoraIso`.
 *   · `apagada` / `inexistente` → SIN platos (`[]`/`{}`) aunque hubiera foto: lo apagado no se enseña,
 *     tampoco horneado.
 *   · cualquier otra cosa (error, red, respuesta rara) → la foto INTACTA y `estado: 'error'`.
 */
export function aplicarFoto(props, resp, ahoraIso) {
    if (!esCartaErp(props)) return props;
    const src = props.source;
    const r = _obj(resp) || {};
    let estado = r.estado;
    if (estado === 'ok' && !_obj(r.carta)) estado = 'error';
    if (estado === 'ok') {
        const pres = presentacionCarta(src);
        const f = fotoDesdeCarta(r.carta,
            { secciones: src.secciones, ocultos: pres.ocultos, destacados: pres.destacados });
        props.categories = f.categories;
        props.carta_alergenos = f.alergenos;
        if (f.local) src.local = f.local;
        src.estado = 'ok';
        src.actualizada = typeof ahoraIso === 'string' ? ahoraIso : '';
    } else if (estado === 'apagada' || estado === 'inexistente') {
        props.categories = [];
        props.carta_alergenos = {};
        src.estado = estado;
    } else {
        src.estado = 'error';
    }
    return props;
}
